import { RecordCollection } from '../../app/record-collection.js';
import {
  Button,
  Card,
  CurrencyInput,
  Input,
  Modal,
  SelectAllToggle,
  Textarea,
  formatCurrency,
  IconButton,
} from '@hue/design-system';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Package, PencilLine, Trash2 } from 'lucide-react';
import { useState } from 'react';
import {
  billingApi,
  getApiError,
  type BillingPeriod,
  type Currency,
  type Plan,
  type PlanPrice,
  type SellableModule,
} from '../../lib/api.js';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  MultiFilter,
  LoadingCard,
  RequestAlert,
} from '../../app/data-view.js';
import { choiceChips } from '../../app/choice-filters.js';
import type { PlansView } from '../../app/list-views.js';
import type { ViewSetter } from '../../app/view-state.js';
import { CreateButton, PageHeader } from '../../app/page-title.js';

const CURRENCIES: Currency[] = ['BRL', 'USD', 'ARS'];
const PERIODS: BillingPeriod[] = ['MONTHLY', 'YEARLY'];

const currencyLabels: Record<Currency, string> = {
  BRL: 'Real',
  USD: 'Dólar',
  ARS: 'Peso argentino',
};

const periodLabels: Record<BillingPeriod, string> = {
  MONTHLY: 'Mensal',
  YEARLY: 'Anual',
};

const activityFilters: Array<{ value: 'ACTIVE' | 'INACTIVE'; label: string }> = [
  { value: 'ACTIVE', label: 'À venda' },
  { value: 'INACTIVE', label: 'Fora de venda' },
];

interface FormState {
  displayName: string;
  description: string;
  active: boolean;
  businessUnitLimit: number;
  /// Sem teto de estabelecimentos; o número fica guardado para quem desmarcar.
  unlimitedUnits: boolean;
  moduleKeys: string[];
  featureKeys: string[];
  /// Preço por moeda e periodicidade, indexado por "MOEDA:PERIODICIDADE". Ausente significa que o
  /// plano não é vendido naquela combinação — diferente de custar zero.
  prices: Record<string, number | null>;
}

function slotOf(currency: Currency, period: BillingPeriod): string {
  return `${currency}:${period}`;
}

function emptyForm(): FormState {
  return {
    displayName: '',
    description: '',
    active: false,
    businessUnitLimit: 1,
    unlimitedUnits: false,
    moduleKeys: [],
    featureKeys: [],
    prices: {},
  };
}

function fromPlan(plan: Plan): FormState {
  return {
    displayName: plan.displayName,
    description: plan.description ?? '',
    active: plan.active,
    businessUnitLimit: plan.businessUnitLimit ?? 1,
    unlimitedUnits: plan.businessUnitLimit === null,
    moduleKeys: [...plan.moduleKeys],
    featureKeys: [...plan.featureKeys],
    prices: Object.fromEntries(
      plan.prices.map((price) => [slotOf(price.currency, price.period), price.amountCents]),
    ),
  };
}

function pricesOf(form: FormState): PlanPrice[] {
  return CURRENCIES.flatMap((currency) =>
    PERIODS.flatMap((period) => {
      const amountCents = form.prices[slotOf(currency, period)];
      return amountCents === null || amountCents === undefined || amountCents <= 0
        ? []
        : [{ currency, period, amountCents }];
    }),
  );
}

export function RootPlans({ view, onView }: { view: PlansView; onView: ViewSetter<PlansView> }) {
  const queryClient = useQueryClient();
  const { search, activity } = view;
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Plan>();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formError, setFormError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Plan>();

  const filters = { ...(search.trim() ? { search: search.trim() } : {}), activity };
  const plans = useQuery({
    queryKey: ['root', 'plans', filters],
    queryFn: () => billingApi.plans(filters),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const modules = useQuery({
    queryKey: ['root', 'modules'],
    queryFn: billingApi.modules,
    retry: false,
  });

  const catalog = modules.data ?? [];

  function openCreate() {
    setEditing(undefined);
    setForm(emptyForm());
    setFormError(undefined);
    setFormOpen(true);
  }

  function openEdit(plan: Plan) {
    setEditing(plan);
    setForm(fromPlan(plan));
    setFormError(undefined);
    setFormOpen(true);
  }

  /// Tirar o módulo leva as características dele junto: vender o acessório sem o miolo entregaria
  /// uma tela que não abre.
  function toggleModule(item: SellableModule) {
    setForm((current) => {
      const chosen = current.moduleKeys.includes(item.key);
      return {
        ...current,
        moduleKeys: chosen
          ? current.moduleKeys.filter((key) => key !== item.key)
          : [...current.moduleKeys, item.key],
        featureKeys: chosen
          ? current.featureKeys.filter(
              (key) => !item.features.some((feature) => feature.key === key),
            )
          : current.featureKeys,
      };
    });
  }

  function toggleFeature(featureKey: string) {
    setForm((current) => ({
      ...current,
      featureKeys: current.featureKeys.includes(featureKey)
        ? current.featureKeys.filter((key) => key !== featureKey)
        : [...current.featureKeys, featureKey],
    }));
  }

  /// Marcar tudo vende o catálogo inteiro, características incluídas; desmarcar esvazia os dois.
  function chooseAll(on: boolean) {
    setForm((current) => ({
      ...current,
      moduleKeys: on ? catalog.map((item) => item.key) : [],
      featureKeys: on ? catalog.flatMap((item) => item.features.map((feature) => feature.key)) : [],
    }));
  }

  function chooseFeatures(item: SellableModule, on: boolean) {
    const keys = item.features.map((feature) => feature.key);
    setForm((current) => ({
      ...current,
      featureKeys: on
        ? [...current.featureKeys, ...keys.filter((key) => !current.featureKeys.includes(key))]
        : current.featureKeys.filter((key) => !keys.includes(key)),
    }));
  }

  async function save() {
    setFormError(undefined);
    const displayName = form.displayName.trim();
    if (displayName === '') return setFormError('Dê um nome ao plano.');

    const prices = pricesOf(form);
    if (form.active && prices.length === 0) {
      return setFormError('Informe ao menos um preço para colocar o plano à venda.');
    }

    const payload = {
      displayName,
      description: form.description.trim() || null,
      active: form.active,
      businessUnitLimit: form.unlimitedUnits ? null : form.businessUnitLimit,
      moduleKeys: form.moduleKeys,
      featureKeys: form.featureKeys,
      prices,
    };

    setSaving(true);
    try {
      if (editing) await billingApi.updatePlan(editing.key, payload);
      else await billingApi.createPlan(payload);
      await queryClient.invalidateQueries({ queryKey: ['root', 'plans'] });
      setFormOpen(false);
    } catch (error) {
      setFormError(getApiError(error));
    } finally {
      setSaving(false);
    }
    return undefined;
  }

  const list = plans.data ?? [];
  const activeFilters = choiceChips('activity', activity, activityFilters, (value) =>
    onView({ activity: value }),
  );

  function planActions(plan: (typeof list)[number]) {
    return (
      <div className="flex flex-wrap justify-end gap-1">
        <IconButton label="Editar" icon={<PencilLine />} onClick={() => openEdit(plan)} />
        <IconButton
          label="Excluir"
          icon={<Trash2 />}
          tone="danger"
          onClick={() => setDeleteTarget(plan)}
        />
      </div>
    );
  }
  return (
    <section aria-labelledby="root-plans-title">
      <PageHeader
        trail="Plataforma / Planos"
        id="root-plans-title"
        title="Planos"
        description="O que cada pacote inclui e por quanto. Mexer aqui não altera quem já contratou."
        actions={<CreateButton onClick={openCreate}>Criar plano</CreateButton>}
      />

      <FilterBar
        searchId="plan-search"
        searchLabel="Buscar plano"
        searchPlaceholder="Nome ou descrição"
        search={search}
        onSearchChange={(value) => onView({ search: value }, { replace: true })}
        chips={activeFilters}
        onClear={() => onView({ activity: [] })}
        count={`${String(list.length)} ${list.length === 1 ? 'plano' : 'planos'}`}
        resultsLabel={`Ver ${String(list.length)} ${list.length === 1 ? 'plano' : 'planos'}`}
      >
        <MultiFilter
          label="Situação"
          itemNoun="situações"
          allLabel="Todas as situações"
          options={activityFilters}
          value={activity}
          onChange={(value) => onView({ activity: value })}
        />
      </FilterBar>

      {plans.isLoading ? (
        <LoadingCard label="Carregando planos" />
      ) : plans.error ? (
        <ErrorCard message={getApiError(plans.error)} />
      ) : list.length ? (
        <RecordCollection
          label="Planos do catálogo"
          records={list}
          recordKey={(plan) => plan.key}
          columns={[
            {
              key: 'name',
              label: 'Plano',
              value: (plan) => plan.displayName,
              render: (plan) => (
                <div>
                  <strong>{plan.displayName}</strong>
                  <p className="text-xs text-content-muted">{plan.key}</p>
                  {plan.description ? <p className="text-xs">{plan.description}</p> : null}
                </div>
              ),
            },
            {
              key: 'units',
              label: 'Unidades ativas',
              value: (plan) => plan.businessUnitLimit ?? Number.MAX_SAFE_INTEGER,
              render: (plan) =>
                plan.businessUnitLimit === null
                  ? 'Sem limite de estabelecimentos'
                  : `Até ${plan.businessUnitLimit} ${plan.businessUnitLimit === 1 ? 'unidade' : 'unidades'}`,
            },
            {
              key: 'includes',
              label: 'Inclui',
              render: (plan) => (
                <div className="flex flex-wrap gap-x-2 gap-y-1 text-xs">
                  {[
                    ...plan.moduleKeys.map(
                      (key) => catalog.find((item) => item.key === key)?.displayName ?? key,
                    ),
                    ...plan.featureKeys.map(
                      (key) =>
                        catalog.flatMap((item) => item.features).find((item) => item.key === key)
                          ?.displayName ?? key,
                    ),
                  ].map((name) => (
                    <span key={name}>{name}</span>
                  ))}
                  {!plan.moduleKeys.length && !plan.featureKeys.length
                    ? 'Só o básico de toda empresa'
                    : null}
                </div>
              ),
            },
            {
              key: 'price',
              label: 'Preços',
              render: (plan) => (
                <div className="grid gap-1 text-xs">
                  {plan.prices.length
                    ? plan.prices.map((price) => (
                        <span key={`${price.currency}:${price.period}`}>
                          <span>{formatCurrency(price.amountCents, price.currency)}</span>{' '}
                          {periodLabels[price.period].toLowerCase()}
                        </span>
                      ))
                    : 'Sem preço'}
                </div>
              ),
            },
            {
              key: 'status',
              label: 'Situação',
              value: (plan) => Number(plan.active),
              render: (plan) => (plan.active ? 'À venda' : 'Fora de venda'),
            },
          ]}
          actions={planActions}
          card={(plan) => (
            <Card key={plan.key} className="bg-surface-card p-5">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <strong className="block text-lg font-semibold">{plan.displayName}</strong>
                  <span className="mt-1 block text-xs text-content-faint">{plan.key}</span>
                </div>
                <span
                  className={`whitespace-nowrap rounded-full border px-3 py-1 text-xs ${
                    plan.active
                      ? 'border-positive/40 bg-positive/12 text-positive'
                      : 'border-surface-border bg-surface-elevated text-content-muted'
                  }`}
                >
                  {plan.active ? 'À venda' : 'Fora de venda'}
                </span>
              </div>

              {plan.description ? (
                <p className="mb-3 text-sm leading-6 text-content-secondary">{plan.description}</p>
              ) : null}

              <dl className="mb-4 grid gap-2 text-xs">
                <div>
                  <dt className="text-content-faint">Unidades ativas</dt>
                  <dd className="mt-1 text-content-secondary">
                    {plan.businessUnitLimit === null
                      ? 'Sem limite de estabelecimentos'
                      : `Até ${plan.businessUnitLimit} ${
                          plan.businessUnitLimit === 1 ? 'unidade' : 'unidades'
                        }`}
                  </dd>
                </div>
                <div>
                  <dt className="text-content-faint">Inclui</dt>
                  <dd className="mt-1 flex flex-wrap gap-1.5">
                    {plan.moduleKeys.length === 0 ? (
                      <span className="text-content-muted">Só o básico de toda empresa</span>
                    ) : (
                      plan.moduleKeys.map((key) => (
                        <span
                          key={key}
                          className="rounded-full border border-surface-border bg-surface-elevated px-2.5 py-1"
                        >
                          {catalog.find((item) => item.key === key)?.displayName ?? key}
                        </span>
                      ))
                    )}
                    {plan.featureKeys.map((key) => (
                      <span
                        key={key}
                        className="rounded-full border border-brand-500/30 bg-brand-500/10 px-2.5 py-1 text-brand-200"
                      >
                        {catalog.flatMap((item) => item.features).find((item) => item.key === key)
                          ?.displayName ?? key}
                      </span>
                    ))}
                  </dd>
                </div>
                <div>
                  <dt className="text-content-faint">Preços</dt>
                  <dd className="mt-1 flex flex-wrap gap-x-4 gap-y-1 tabular-nums">
                    {plan.prices.length === 0 ? (
                      <span className="text-content-muted">Sem preço</span>
                    ) : (
                      plan.prices.map((price) => (
                        <span key={`${price.currency}:${price.period}`}>
                          {formatCurrency(price.amountCents, price.currency)}{' '}
                          <span className="text-content-faint">
                            {periodLabels[price.period].toLowerCase()}
                          </span>
                        </span>
                      ))
                    )}
                  </dd>
                </div>
              </dl>

              {planActions(plan)}
            </Card>
          )}
        />
      ) : (
        <EmptyCard
          icon={Package}
          title="Nenhum plano encontrado"
          description="Ajuste os filtros ou monte o primeiro pacote à venda."
        />
      )}

      <Modal
        open={formOpen}
        onOpenChange={setFormOpen}
        title={editing ? 'Editar plano' : 'Criar plano'}
        description="Escolha o que entra e defina o preço em cada moeda."
      >
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <div className="grid gap-5">
            <Input
              label="Nome"
              autoComplete="off"
              value={form.displayName}
              onChange={(event) =>
                setForm((current) => ({ ...current, displayName: event.target.value }))
              }
              {...(editing ? { hint: `Chave técnica: ${editing.key} (não muda).` } : {})}
            />
            <Textarea
              label="Descrição"
              rows={2}
              value={form.description}
              onChange={(event) =>
                setForm((current) => ({ ...current, description: event.target.value }))
              }
            />
            <div className="grid gap-3">
              {form.unlimitedUnits ? null : (
                <Input
                  label="Limite de unidades ativas"
                  type="number"
                  min={1}
                  max={999}
                  value={String(form.businessUnitLimit)}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      businessUnitLimit: Number(event.target.value),
                    }))
                  }
                  hint="Desativar uma unidade libera uma vaga sem apagar seus dados."
                />
              )}
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1 cursor-pointer accent-brand-500"
                  checked={form.unlimitedUnits}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, unlimitedUnits: event.target.checked }))
                  }
                />
                <span>
                  <strong className="block text-sm font-medium">
                    Sem limite de estabelecimentos
                  </strong>
                  <span className="mt-0.5 block text-xs text-content-muted">
                    A empresa abre quantos estabelecimentos quiser. Unidade de apoio nunca conta.
                  </span>
                </span>
              </label>
            </div>

            <fieldset>
              <legend className="float-left mb-2 text-sm font-medium">O que o plano inclui</legend>
              <SelectAllToggle
                className="float-right"
                scope="o que o plano inclui"
                total={catalog.length}
                selected={catalog.filter((item) => form.moduleKeys.includes(item.key)).length}
                onSelectAll={() => chooseAll(true)}
                onClear={() => chooseAll(false)}
              />
              <p className="clear-both mb-3 text-xs text-content-muted">
                Login, perfis de acesso e auditoria acompanham toda empresa e não entram na escolha.
              </p>
              <div className="grid gap-3">
                {catalog.map((item) => {
                  const chosen = form.moduleKeys.includes(item.key);
                  return (
                    <div
                      key={item.key}
                      className="rounded-xl border border-surface-border bg-surface-elevated p-3"
                    >
                      <label className="flex cursor-pointer items-start gap-3">
                        <input
                          type="checkbox"
                          className="mt-1 cursor-pointer accent-brand-500"
                          checked={chosen}
                          onChange={() => toggleModule(item)}
                        />
                        <span>
                          <strong className="block text-sm font-medium">{item.displayName}</strong>
                          <span className="mt-0.5 block text-xs text-content-muted">
                            {item.description}
                          </span>
                        </span>
                      </label>

                      {chosen && item.features.length > 0 ? (
                        <div className="mt-3 grid gap-2 border-t border-surface-border pl-7 pt-3">
                          <SelectAllToggle
                            className="justify-self-end"
                            scope={`características de ${item.displayName}`}
                            total={item.features.length}
                            selected={
                              item.features.filter((feature) =>
                                form.featureKeys.includes(feature.key),
                              ).length
                            }
                            onSelectAll={() => chooseFeatures(item, true)}
                            onClear={() => chooseFeatures(item, false)}
                          />
                          {item.features.map((feature) => (
                            <label
                              key={feature.key}
                              className="flex cursor-pointer items-start gap-3"
                            >
                              <input
                                type="checkbox"
                                className="mt-1 cursor-pointer accent-brand-500"
                                checked={form.featureKeys.includes(feature.key)}
                                onChange={() => toggleFeature(feature.key)}
                              />
                              <span>
                                <strong className="block text-xs font-medium">
                                  {feature.displayName}
                                </strong>
                                <span className="mt-0.5 block text-xs text-content-faint">
                                  {feature.description}
                                </span>
                              </span>
                            </label>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </fieldset>

            <fieldset>
              <legend className="mb-2 text-sm font-medium">Preços</legend>
              <p className="mb-3 text-xs text-content-muted">
                Deixe em branco a combinação em que o plano não é vendido. Valor em branco não é o
                mesmo que preço zero.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                {CURRENCIES.flatMap((currency) =>
                  PERIODS.map((period) => (
                    <CurrencyInput
                      key={slotOf(currency, period)}
                      label={`${currencyLabels[currency]} · ${periodLabels[period]}`}
                      value={form.prices[slotOf(currency, period)] ?? null}
                      onValueChange={(cents) =>
                        setForm((current) => ({
                          ...current,
                          prices: { ...current.prices, [slotOf(currency, period)]: cents },
                        }))
                      }
                    />
                  )),
                )}
              </div>
            </fieldset>

            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                className="mt-1 cursor-pointer accent-brand-500"
                checked={form.active}
                onChange={(event) =>
                  setForm((current) => ({ ...current, active: event.target.checked }))
                }
              />
              <span>
                <strong className="block text-sm font-medium">Colocar à venda</strong>
                <span className="mt-0.5 block text-xs text-content-muted">
                  Tirar da venda não afeta quem já contratou.
                </span>
              </span>
            </label>
          </div>

          {formError ? <RequestAlert message={formError} /> : null}

          <div className="mt-7 flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
            <Button
              className="w-full sm:w-auto"
              type="button"
              variant="outline"
              onClick={() => setFormOpen(false)}
            >
              Cancelar
            </Button>
            <Button className="w-full sm:w-auto" loading={saving}>
              {editing ? 'Salvar plano' : 'Criar plano'}
            </Button>
          </div>
        </form>
      </Modal>

      {deleteTarget ? (
        <DeletePlanDialog
          plan={deleteTarget}
          onClose={() => setDeleteTarget(undefined)}
          onDeleted={async () => {
            await queryClient.invalidateQueries({ queryKey: ['root', 'plans'] });
            setDeleteTarget(undefined);
          }}
        />
      ) : null}
    </section>
  );
}

function DeletePlanDialog({
  plan,
  onClose,
  onDeleted,
}: {
  plan: Plan;
  onClose: () => void;
  onDeleted: () => Promise<void>;
}) {
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function confirm() {
    setError(undefined);
    setBusy(true);
    try {
      await billingApi.deletePlan(plan.key);
      await onDeleted();
    } catch (requestError) {
      setError(getApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={onClose}
      title="Excluir plano"
      description="O plano sai do catálogo. Quem já contratou continua com o que tem."
    >
      <p className="text-sm leading-6 text-content-secondary">
        Excluir <strong>{plan.displayName}</strong> do catálogo? Empresas que estejam neste plano
        impedem a exclusão, para o histórico da contratação não ficar sem referência.
      </p>
      {error ? <RequestAlert message={error} /> : null}
      <div className="mt-7 flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
        <Button className="w-full sm:w-auto" type="button" variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          className="w-full sm:w-auto"
          type="button"
          loading={busy}
          onClick={() => void confirm()}
        >
          Excluir plano
        </Button>
      </div>
    </Modal>
  );
}
